import { useEffect, useMemo } from "react";
import { useTranslations } from "@acto/web-common/locales";
import type { MeetingPrepLearnedDefault } from "@acto/taskpane/core/domain";
import { useSessionTelemetry } from "@acto/taskpane/core/telemetry/SessionTelemetryProvider";
import {
  cardNameLabel,
  isNamedCardKey,
} from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/cardLabel";
import type { ProfileEntry } from "./ProfileSection";

const SEPARATOR = " · ";

export function useLearnedDefaultEntries(learnedDefaults: MeetingPrepLearnedDefault[] | undefined): ProfileEntry[] {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.learnedDefault");
  const tCardHide = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");
  const telemetry = useSessionTelemetry();

  const unnamedCardKeys = useMemo(
    () => (learnedDefaults ?? []).map(({ cardKey }) => cardKey).filter((cardKey) => !isNamedCardKey(cardKey)),
    [learnedDefaults],
  );

  useEffect(() => {
    for (const cardKey of unnamedCardKeys) {
      void telemetry?.recordError("handled", new Error(`Learned default for a card key without a name: ${cardKey}`), {
        feature: "meeting-prep-learned-defaults",
        card_key: cardKey,
      });
    }
  }, [telemetry, unnamedCardKeys]);

  return useMemo(
    () =>
      (learnedDefaults ?? []).flatMap(({ id, cardKey, learnedAfterHides }) =>
        isNamedCardKey(cardKey)
          ? [
              {
                id,
                title: cardNameLabel(cardKey, tCardHide),
                meta: [
                  t("scopeAllMyClients"),
                  t("durationForever"),
                  t("learnedAfterHides", { count: learnedAfterHides }),
                ].join(SEPARATOR),
              },
            ]
          : [],
      ),
    [learnedDefaults, t, tCardHide],
  );
}
